import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  INotification,
  INotificationServer,
  notificationFilter,
  NOTIFICATIONS_QUERY_KEYS,
} from '@/api/notifications/types';
import { useNotificationsSocket } from '@/api/socket/notifications/use-notifications-socket';
import { queryClient } from '@/app/providers/react-query-provider';
import BellIcon from '@/assets/svg/header/bell.svg';
import QRCodeIcon from '@/assets/svg/ui-elements/qr-code.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { settingsTabKeys } from '@/pages/settings/settings-tab-keys';
import { useGetAllNotifications } from '@/pages/settings/utils/use-get-all-notifications';
import { PagePaths } from '@/routes/page-paths';
import { IconButton } from '@/shared/design-system';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { isNotificationForActiveSession } from '@/utils/notification-scope';

import { ActionsContainer, BadgeAnchor, NotificationBadge } from './HeaderActions.styled';

export const HeaderActions = () => {
  const navigate = useNavigate();
  const { handleOpen: openQRCode } = useGlobalModal('QR_CODE_MODAL');
  const notifications = useGetAllNotifications({ filter: notificationFilter.NEW });
  const hasNotifications = notifications.length > 0;

  const { selectedUserRole, selectedEntityId } = useSelectedUserConfigSlice();
  const { isConnected, on, off } = useNotificationsSocket();

  useEffect(() => {
    if (!isConnected) return;

    const handleNewMessage = (notification: INotification) => {
      if (!isNotificationForActiveSession(notification, selectedUserRole, selectedEntityId)) {
        return;
      }

      queryClient.setQueryData(
        [
          NOTIFICATIONS_QUERY_KEYS.notifications,
          { limit: 50, page: 1, filter: notificationFilter.NEW },
        ],
        (oldData: INotificationServer[]) => {
          if (!oldData) return oldData;
          return [notification, ...oldData];
        },
      );
    };

    on('notification:new', handleNewMessage);
    return () => off('notification:new', handleNewMessage);
  }, [isConnected, on, off, selectedUserRole, selectedEntityId]);

  return (
    <ActionsContainer>
      <BadgeAnchor>
        <IconButton
          color="tertiary"
          variant="solid"
          size="xs"
          aria-label="open notifications"
          onClick={() => navigate(`${PagePaths.settings}?tab=${settingsTabKeys.notifications}`)}
        >
          <BellIcon />
        </IconButton>
        {hasNotifications && <NotificationBadge />}
      </BadgeAnchor>

      <IconButton
        color="tertiary"
        variant="solid"
        size="xs"
        aria-label="open QR code"
        onClick={openQRCode}
      >
        <QRCodeIcon />
      </IconButton>
    </ActionsContainer>
  );
};
